import { useState, FC, forwardRef } from 'react';

import { Popover } from 'react-tiny-popover';

import useSearchbar from '@contexts/searchbar';
import SelectedComponent from '@shared/searchBar/datePicker/selectedComponent';
import { LocationAnimationErrorBox } from '@components/views/components/listing/ListingStyle';
import {
  SearchBarInputProps,
  SearchBarItemProps,
} from '@components/ui/search/SearchBarInput/SearchBarInputInterface';
import {
  StyledSearchBarWrapper,
  RenderOverlayContentWrapper,
} from '@components/ui/search/SearchBarInput/SearchBarInputStyle';
import EmptyComp from '@shared/searchBar/datePicker/emptyComp';
import { formatShortLocalDate } from '@utils/dateUtil';

export const SearchBarItem: FC<SearchBarItemProps> = ({
  InputComponent,
  onFocusChange,
}) => {
  const dateData = InputComponent?.toString().split('-');
  const startDate = dateData?.[0];
  const endDate = dateData?.[1];
  const {
    state: { flight: state },
  } = useSearchbar();

  const handleFocusChange = (focus: 'startDate' | 'endDate') => {
    if (onFocusChange) {
      onFocusChange(focus);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: '8px',
      }}
    >
      <SelectedComponent
        value={formatShortLocalDate(startDate)}
        label='Gidiş'
        isOneWay={state.isOneWay}
        rightBorder={true}
        onClick={() => handleFocusChange('startDate')}
      />

      {state.isOneWay ? null : endDate === ' ' ? (
        <EmptyComp label='Dönüş' onClick={() => handleFocusChange('endDate')} />
      ) : (
        <SelectedComponent
          value={formatShortLocalDate(endDate)}
          label='Dönüş'
          rightBorder={true}
          onClick={() => handleFocusChange('endDate')}
        />
      )}
    </div>
  );
};

export interface CustomPopoverChildComponentProps
  extends React.ComponentPropsWithoutRef<'div'> {
  onClick(): void;
}

const CustomPopoverChildComponent = forwardRef<
  HTMLDivElement,
  CustomPopoverChildComponentProps
>((props, ref) => (
  <StyledSearchBarWrapper
    ref={ref}
    onClick={props.onClick}
    data-testid='flight-date-selection-button'
  >
    {props.children}
  </StyledSearchBarWrapper>
));

const FlightSearchBarInput: FC<SearchBarInputProps> = ({
  InputComponent,
  renderOverlayContent,
  error,
  onFocusChange,
  popoverContentReady = true,
  popoverProps: {
    //defaultIsOpen and isOpen should not be set together.
    isOpen, //controlled popover
    defaultIsOpen, //un-controlled popover
    padding = 10,
    onVisibleChange,
    onClickOutside,
    containerStyle,
    ...otherPopoverProps
  } = {},
}) => {
  const [isOverlayOpen, setIsOverlayOpen] = useState<boolean>(!!defaultIsOpen);

  const isControlledPopover = typeof isOpen === 'boolean';

  const closeOverlay = () => {
    isControlledPopover ? onVisibleChange?.(false) : setIsOverlayOpen(false);
  };

  const toggleOverlay = () => {
    isControlledPopover
      ? onVisibleChange?.(!isOpen)
      : setIsOverlayOpen(!isOverlayOpen);
  };

  const isOverlayVisible = isControlledPopover ? !!isOpen : isOverlayOpen;
  const isPopoverContentVisible = isOverlayVisible && popoverContentReady;

  return (
    <div>
      <Popover
        positions={['bottom', 'left']}
        align='start'
        padding={padding}
        containerStyle={{
          zIndex: '99',
          backgroundColor: 'white',
          display: 'flex',
          justifyContent: 'center',
          margin: '0px',
          boxShadow: '0 20px 40px 0 rgba(0, 0, 0, 0.2)',
          top: '-9px',
          ...containerStyle,
          opacity: isPopoverContentVisible ? '1' : '0',
          pointerEvents: isPopoverContentVisible ? 'auto' : 'none',
        }}
        reposition={false}
        content={() => {
          return renderOverlayContent ? (
            <RenderOverlayContentWrapper
              aria-hidden={!isPopoverContentVisible}
              inert={!isPopoverContentVisible ? true : undefined}
            >
              {renderOverlayContent(closeOverlay)}
            </RenderOverlayContentWrapper>
          ) : (
            <div /> //TODO: it will never render, ts null error fix
          );
        }}
        isOpen={isOverlayVisible}
        onClickOutside={(e) => {
          onClickOutside?.(e);
          closeOverlay();
        }}
        {...otherPopoverProps}
      >
        <CustomPopoverChildComponent onClick={toggleOverlay}>
          <LocationAnimationErrorBox hasError={error || false}>
            <SearchBarItem
              onFocusChange={onFocusChange}
              InputComponent={InputComponent}
            />
          </LocationAnimationErrorBox>
        </CustomPopoverChildComponent>
      </Popover>
    </div>
  );
};

export default FlightSearchBarInput;
